import React from "react";
import { AiFillStar } from "react-icons/ai";
import {
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaRegCalendarAlt,
  FaRegClock,
  FaUserAlt,
} from "react-icons/fa";

// Format total mins → "1 hr 30 mins"
const formatDuration = (mins) => {
  const hours = Math.floor(mins / 60);
  const minutes = mins % 60;
  if (hours && minutes) return `${hours} hr ${minutes} mins`;
  if (hours) return `${hours} hr`;
  return `${minutes} mins`;
};

// Extract mins from desc like "30 mins", "1 hr", "1 hr 30 mins"
const extractMinutes = (desc) => {
  const lower = desc?.toLowerCase() || "";
  const hrMatch = lower.match(/(\d+)\s*hr/);
  const minMatch = lower.match(/(\d+)\s*min/);

  const hr = hrMatch ? parseInt(hrMatch[1], 10) : 0;
  const min = minMatch ? parseInt(minMatch[1], 10) : 0;

  return hr * 60 + min;
};

const SummaryCard = ({
  name,
  email,
  mobile,
  selectedNailServices,
  selectedBeautyServices,
  selectedProfessionalId,
  staffs,
  selectedDateTime,
  message,
  store,
}) => {
  console.log({ store });
  const renderServiceListWithTime = (services) => {
    if (!services.length)
      return <p className="text-base text-gray-400 italic">None selected</p>;

    const totalMins = services.reduce(
      (acc, s) => acc + extractMinutes(s.desc),
      0
    );

    return (
      <>
        {services.map((item, i) => (
          <div key={i} className="flex items-start space-x-2 mb-2">
            <div className="w-6 h-6 bg-yellowlight rounded-md flex items-center justify-center mt-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-yellowdark"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <p className="text-lg font-semibold text-white">
              {item.title}
              {item.desc && (
                <span className="text-sm text-gray-400"> ({item.desc})</span>
              )}
            </p>
          </div>
        ))}
        <p className="text-right text-yellowlight font-medium mt-2">
          Total: {formatDuration(totalMins)}
        </p>
      </>
    );
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    if (isNaN(date)) return dateStr;

    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  };

  const totalDurationMins =
    selectedNailServices.reduce((acc, s) => acc + extractMinutes(s.desc), 0) +
    selectedBeautyServices.reduce((acc, s) => acc + extractMinutes(s.desc), 0);

  //

  const formatDateRange = (startStr, totalMins) => {
    const [datePart, timePart] = startStr.split(" ");
    const [year, month, day] = datePart.split("-").map(Number);
    let [hour, minute] = timePart.split(":").map(Number);
    const isPM = startStr.toLowerCase().includes("pm");

    if (isPM && hour < 12) hour += 12;
    if (!isPM && hour === 12) hour = 0;

    const start = new Date(year, month - 1, day, hour, minute);
    const end = new Date(start.getTime() + totalMins * 60000);

    const dateOptions = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    const timeOptions = { hour: "numeric", minute: "2-digit", hour12: true };

    const datePartFormatted = start.toLocaleDateString("en-US", dateOptions);
    const startTime = start.toLocaleTimeString("en-US", timeOptions);
    const endTime = end.toLocaleTimeString("en-US", timeOptions);

    return {
      datePart: datePartFormatted,
      timeRange: `${startTime} – ${endTime}`,
    };
  };

  const SectionCard = ({ title, children }) => (
    <div className="bg-white/10 p-4 rounded-2xl shadow border border-border">
      <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-2">
        {title}
      </h4>
      <div>{children}</div>
    </div>
  );

  return (
    <div className="space-y-6 p-6 border border-border rounded-2xl shadow-lg bg-dry dark:bg-neutral-900">
      <h2 className="text-xl font-bold text-yellowdark dark:text-yellowlight border-b border-border pb-4">
        Booking Summary
      </h2>
      {/* Nail Services with Total Duration */}
      <SectionCard title="Nail Services">
        {renderServiceListWithTime(selectedNailServices)}
      </SectionCard>
      {/* Beauty Services with Total Duration */}
      <SectionCard title="Beauty Services">
        {renderServiceListWithTime(selectedBeautyServices)}
      </SectionCard>
      {/* Preferred Professional */}
      <SectionCard title="Preferred Professional">
        {selectedProfessionalId?._id === "any" ? (
          <p className="text-base text-gray-400 italic">Any available</p>
        ) : selectedProfessionalId?._id === "per-service" ? (
          <p className="text-base text-gray-400 italic">Selected per service</p>
        ) : (
          (() => {
            const staff = staffs.find(
              (s) => s._id === selectedProfessionalId?._id
            );
            return staff ? (
              <div className="flex items-center space-x-3 mt-2">
                <img
                  src={staff.photo}
                  alt={staff.name}
                  className="w-10 h-10 rounded-full object-cover border border-yellowlight"
                />
                <div className="flex flex-col">
                  <p className="text-lg font-semibold text-white">
                    {staff.name}
                  </p>
                  {staff.role && (
                    <p className="text-sm text-gray-400">{staff.role}</p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-base text-gray-400 italic">Unknown staff</p>
            );
          })()
        )}
      </SectionCard>
      {/* Appointment Time */}
      <SectionCard title="Appointment Time">
        {selectedDateTime ? (
          (() => {
            const { datePart, timeRange } = formatDateRange(
              selectedDateTime,
              totalDurationMins
            );

            return (
              <div className="flex flex-col space-y-2">
                <div className="flex items-center space-x-4">
                  <FaRegCalendarAlt className="text-white" />
                  <p className="text-white font-semibold text-xl">{datePart}</p>
                </div>
                <div className="flex items-center space-x-4">
                  <FaRegClock className="text-white" />
                  <p className="text-yellowlight font-semibold text-xl">
                    {timeRange}
                  </p>
                </div>
              </div>
            );
          })()
        ) : (
          <p className="text-base text-gray-400 italic">Not selected</p>
        )}
      </SectionCard>

      {/* Contact Info */}
      <SectionCard title="Your Contact Info">
        <ul className="space-y-3">
          <li className="flex items-start space-x-3">
            <FaUserAlt className="text-white mt-1" />
            <div>
              <p className="text-sm text-gray-400">
                {" "}
                <span className="text-lg font-semibold text-white uppercase">
                  {name || "—"}
                </span>
              </p>
              <p className="text-sm text-gray-400">
                {" "}
                <span className="text-lg  text-textGray">{email || "—"}</span>
              </p>
              <p className="text-sm text-gray-400">
                <span className="text-lg  text-textGray">{mobile || "—"}</span>
              </p>
            </div>
          </li>
        </ul>
      </SectionCard>
      {/* Store location */}
      <SectionCard title="Location">
        <div className="flex items-start space-x-3">
          <FaMapMarkerAlt className="text-white mt-1" />
          <div className="flex flex-col">
            <p className="text-lg font-semibold text-white uppercase">
              {store[0].title}
            </p>
            <p className="text-base text-textGray">{store[0].links[0]}</p>
            <p className="text-base text-textGray">{store[0].tel_no}</p>
          </div>
        </div>
      </SectionCard>
      {/* Additional Notes */}
      <SectionCard title="Additional Notes">
        <p className="text-lg font-semibold text-white">{message || "—"}</p>
      </SectionCard>
    </div>
  );
};

export default SummaryCard;
